/**
 * CALCULADOR DE NUTRICIÓN - LÓGICA CENTRALIZADA
 * Este módulo contiene toda la lógica de cálculo de macros y porciones
 * según las especificaciones del sistema de nutrición.
 * 
 * REGLAS DE MEDICIÓN:
 * - Proteínas: valores en CRUDO
 * - Carbohidratos: cocido (arroz, pasta, papa) o crudo/polvo (avena, crema de arroz)
 * - Grasas: en GRAMOS
 * - Frutas: en CRUDO (cuenta como carbohidratos)
 * - Vegetales: libres, sugerido 100-200g, NO afectan macros
 */

import { FOOD_DATABASE, filterFoodsByDietType } from '../data/foodDatabase';

// ============================================================
// PASO 1: DIVISIÓN AUTOMÁTICA DE MACROS POR COMIDA
// ============================================================

/**
 * Divide los macros totales del día entre el número de comidas
 * @param {Object} totalMacros - { calorias, proteinas, carbohidratos, grasas }
 * @param {number} numberOfMeals - Número de comidas del día
 * @returns {Object} Macros por comida
 */
export const divideMacrosByMeals = (totalMacros, numberOfMeals) => {
  if (numberOfMeals <= 0) return totalMacros;
  
  return {
    calorias: Math.round(totalMacros.calorias / numberOfMeals),
    proteinas: Math.round(totalMacros.proteinas / numberOfMeals),
    carbohidratos: Math.round(totalMacros.carbohidratos / numberOfMeals),
    grasas: Math.round(totalMacros.grasas / numberOfMeals)
  };
};

// ============================================================
// PASO 3: CÁLCULO DE GRAMOS
// Formula: GRAMOS = (meta_macro / macro_por_100g) × 100
// ============================================================

/**
 * Calcula los gramos necesarios de un alimento para alcanzar un macro objetivo
 * @param {number} targetMacro - Cantidad objetivo del macro (en gramos)
 * @param {number} macroPer100g - Cantidad del macro por 100g del alimento
 * @returns {number} Gramos necesarios del alimento
 */
export const calculateGramsForMacro = (targetMacro, macroPer100g) => {
  if (macroPer100g <= 0) return 0;
  return (targetMacro / macroPer100g) * 100;
};

// ============================================================
// PASO 4: CÁLCULO DE PROTEÍNA
// ============================================================

/**
 * Calcula los gramos de proteína necesarios y la grasa que aporta
 * @param {Object} food - Alimento de la base de datos
 * @param {number} targetProtein - Proteína objetivo de la comida
 * @returns {Object} { grams, fatContributed }
 */
export const calculateProteinPortion = (food, targetProtein) => {
  if (!food || food.proteinas <= 0) {
    return { grams: 0, fatContributed: 0, actualProtein: 0 };
  }
  
  // Calcular gramos necesarios para alcanzar la meta de proteína
  const gramsNeeded = calculateGramsForMacro(targetProtein, food.proteinas);
  
  // Calcular la grasa que aporta esa cantidad de proteína
  // Grasa aportada = (gramos proteína × grasa por 100g) / 100
  const fatContributed = (gramsNeeded * food.grasas) / 100;
  
  // Proteína real que aporta
  const actualProtein = (gramsNeeded * food.proteinas) / 100;
  
  return {
    grams: Math.round(gramsNeeded),
    fatContributed: fatContributed,
    actualProtein: actualProtein
  };
};

// ============================================================
// PASO 5: CÁLCULO DE CARBOHIDRATO
// ============================================================

/**
 * Calcula los gramos de carbohidrato considerando si hay fruta
 * @param {Object} food - Alimento de la base de datos
 * @param {number} targetCarbs - Carbohidratos objetivo de la comida
 * @param {number} fruitCarbs - Carbohidratos aportados por la fruta (0 si no hay)
 * @returns {Object} { grams, actualCarbs }
 */
export const calculateCarbPortion = (food, targetCarbs, fruitCarbs = 0) => {
  if (!food || food.carbohidratos <= 0) {
    return { grams: 0, actualCarbs: 0 };
  }
  
  // Carbohidratos faltantes = Meta - carbohidratos de fruta
  const remainingCarbs = Math.max(targetCarbs - fruitCarbs, 0);
  
  // Calcular gramos necesarios
  const gramsNeeded = calculateGramsForMacro(remainingCarbs, food.carbohidratos);
  
  // Carbohidratos reales
  const actualCarbs = (gramsNeeded * food.carbohidratos) / 100;
  
  return {
    grams: Math.round(gramsNeeded),
    actualCarbs: actualCarbs,
    remainingCarbs: remainingCarbs
  };
};

// ============================================================
// PASO 6: GRASA INTELIGENTE (OBLIGATORIO)
// ============================================================

/**
 * Calcula si se necesita grasa adicional y cuánta
 * La grasa de la proteína se resta de la meta total de grasa
 * 
 * @param {number} targetFat - Meta de grasa de la comida
 * @param {number} fatFromProtein - Grasa aportada por la proteína seleccionada
 * @param {Object} fatFood - Alimento de grasa seleccionado (puede ser null)
 * @returns {Object} { needsAdditionalFat, grams, remainingFat, message }
 */
export const calculateSmartFat = (targetFat, fatFromProtein, fatFood = null) => {
  // Calcular grasa faltante
  const remainingFat = targetFat - fatFromProtein;
  
  // Si grasa faltante <= 0, no se necesita grasa adicional
  if (remainingFat <= 0) {
    return {
      needsAdditionalFat: false,
      grams: 0,
      remainingFat: 0,
      message: 'No necesitas grasa adicional en esta comida.'
    };
  }
  
  // Si se necesita grasa adicional y hay un alimento seleccionado
  if (fatFood && fatFood.grasas > 0) {
    const gramsNeeded = calculateGramsForMacro(remainingFat, fatFood.grasas);
    return {
      needsAdditionalFat: true,
      grams: Math.round(gramsNeeded),
      remainingFat: remainingFat,
      message: null
    };
  }
  
  // Se necesita grasa pero no hay alimento seleccionado
  return {
    needsAdditionalFat: true,
    grams: 0,
    remainingFat: remainingFat,
    message: `Necesitas ${Math.round(remainingFat)}g de grasa adicional.`
  };
};

// ============================================================
// PASO 8: REDONDEO VISUAL
// ============================================================

/**
 * Redondea un valor según el tipo de macro para visualización
 * - Proteína y carbohidratos: múltiplos de 5g
 * - Grasas: redondear a 1g
 * 
 * @param {number} value - Valor a redondear
 * @param {string} type - 'proteina' | 'carbohidrato' | 'grasa'
 * @returns {number} Valor redondeado
 */
export const roundForDisplay = (value, type) => {
  if (type === 'grasa') {
    return Math.round(value);
  }
  // Proteína y carbohidratos: múltiplos de 5
  return Math.round(value / 5) * 5;
};

/**
 * Redondea los gramos de un alimento para visualización
 * Los gramos se redondean a múltiplos de 5 para facilitar la medición
 * @param {number} grams - Gramos calculados
 * @returns {number} Gramos redondeados
 */
export const roundGramsForDisplay = (grams) => {
  return Math.round(grams / 5) * 5;
};

// ============================================================
// CÁLCULO COMPLETO DE UNA COMIDA
// ============================================================

/**
 * Calcula todas las porciones de una comida completa
 * Implementa la lógica de "Grasa Inteligente"
 * 
 * @param {Object} mealTargets - { proteinas, carbohidratos, grasas, calorias }
 * @param {Object} selectedFoods - { proteina, carbohidrato, grasa, fruta }
 * @param {boolean} hasFruit - Si esta comida incluye fruta
 * @returns {Object} Resultado completo con porciones y macros
 */
export const calculateMealPortions = (mealTargets, selectedFoods, hasFruit = false) => {
  const result = {
    proteina: { grams: 0, rawGrams: 0, macros: { proteinas: 0, carbohidratos: 0, grasas: 0, calorias: 0 } },
    carbohidrato: { grams: 0, rawGrams: 0, macros: { proteinas: 0, carbohidratos: 0, grasas: 0, calorias: 0 } },
    grasa: { grams: 0, rawGrams: 0, macros: { proteinas: 0, carbohidratos: 0, grasas: 0, calorias: 0 }, disabled: false, message: null },
    fruta: { grams: 0, rawGrams: 0, macros: { proteinas: 0, carbohidratos: 0, grasas: 0, calorias: 0 } },
    vegetal: { grams: '100-200', message: 'Sugerido 100-200g' },
    totals: { proteinas: 0, carbohidratos: 0, grasas: 0, calorias: 0 }
  };
  
  let fatFromProtein = 0;
  let carbsFromFruit = 0;
  
  // 1. CALCULAR PROTEÍNA
  if (selectedFoods.proteina) {
    const proteinCalc = calculateProteinPortion(selectedFoods.proteina, mealTargets.proteinas);
    result.proteina.rawGrams = proteinCalc.grams;
    result.proteina.grams = roundGramsForDisplay(proteinCalc.grams);
    
    // Calcular macros aportados
    const mult = proteinCalc.grams / 100;
    result.proteina.macros = {
      proteinas: selectedFoods.proteina.proteinas * mult,
      carbohidratos: selectedFoods.proteina.carbohidratos * mult,
      grasas: selectedFoods.proteina.grasas * mult,
      calorias: selectedFoods.proteina.calorias * mult
    };
    
    fatFromProtein = proteinCalc.fatContributed;
  }
  
  // 2. CALCULAR FRUTA (si aplica) - La fruta cuenta como carbohidratos
  if (hasFruit && selectedFoods.fruta) {
    // Para frutas, calculamos para aportar ~15g de carbohidratos (porción estándar)
    const targetFruitCarbs = 15;
    const fruitGrams = calculateGramsForMacro(targetFruitCarbs, selectedFoods.fruta.carbohidratos);
    result.fruta.rawGrams = fruitGrams;
    result.fruta.grams = roundGramsForDisplay(fruitGrams);
    
    const mult = fruitGrams / 100;
    result.fruta.macros = {
      proteinas: selectedFoods.fruta.proteinas * mult,
      carbohidratos: selectedFoods.fruta.carbohidratos * mult,
      grasas: selectedFoods.fruta.grasas * mult,
      calorias: selectedFoods.fruta.calorias * mult
    };
    
    carbsFromFruit = result.fruta.macros.carbohidratos;
  }
  
  // 3. CALCULAR CARBOHIDRATO (restando los carbos de la fruta)
  if (selectedFoods.carbohidrato) {
    const carbCalc = calculateCarbPortion(selectedFoods.carbohidrato, mealTargets.carbohidratos, carbsFromFruit);
    result.carbohidrato.rawGrams = carbCalc.grams;
    result.carbohidrato.grams = roundGramsForDisplay(carbCalc.grams);
    
    const mult = carbCalc.grams / 100;
    result.carbohidrato.macros = {
      proteinas: selectedFoods.carbohidrato.proteinas * mult,
      carbohidratos: selectedFoods.carbohidrato.carbohidratos * mult,
      grasas: selectedFoods.carbohidrato.grasas * mult,
      calorias: selectedFoods.carbohidrato.calorias * mult
    };
  }
  
  // 4. CALCULAR GRASA INTELIGENTE
  const smartFat = calculateSmartFat(mealTargets.grasas, fatFromProtein, selectedFoods.grasa);
  
  if (!smartFat.needsAdditionalFat) {
    // No se necesita grasa adicional
    result.grasa.disabled = true;
    result.grasa.message = smartFat.message;
    result.grasa.grams = 0;
  } else if (selectedFoods.grasa) {
    // Se necesita grasa y hay una seleccionada
    result.grasa.rawGrams = smartFat.grams;
    result.grasa.grams = Math.round(smartFat.grams); // Grasas se redondean a 1g
    result.grasa.disabled = false;
    
    const mult = smartFat.grams / 100;
    result.grasa.macros = {
      proteinas: selectedFoods.grasa.proteinas * mult,
      carbohidratos: selectedFoods.grasa.carbohidratos * mult,
      grasas: selectedFoods.grasa.grasas * mult,
      calorias: selectedFoods.grasa.calorias * mult
    };
  } else {
    // Se necesita grasa pero no hay seleccionada
    result.grasa.message = smartFat.message;
    result.grasa.remainingFat = smartFat.remainingFat;
  }
  
  // 5. CALCULAR TOTALES
  result.totals = {
    proteinas: Math.round(
      result.proteina.macros.proteinas +
      result.carbohidrato.macros.proteinas +
      result.grasa.macros.proteinas +
      result.fruta.macros.proteinas
    ),
    carbohidratos: Math.round(
      result.proteina.macros.carbohidratos +
      result.carbohidrato.macros.carbohidratos +
      result.grasa.macros.carbohidratos +
      result.fruta.macros.carbohidratos
    ),
    grasas: Math.round(
      result.proteina.macros.grasas +
      result.carbohidrato.macros.grasas +
      result.grasa.macros.grasas +
      result.fruta.macros.grasas
    ),
    calorias: Math.round(
      result.proteina.macros.calorias +
      result.carbohidrato.macros.calorias +
      result.grasa.macros.calorias +
      result.fruta.macros.calorias
    )
  };
  
  return result;
};

// ============================================================
// HELPER: OBTENER ALIMENTO POR ID
// ============================================================

/**
 * Busca un alimento por su ID en la categoría correspondiente
 * @param {string} category - 'proteina' | 'carbohidrato' | 'grasa' | 'fruta' | 'vegetal'
 * @param {string} foodId - ID del alimento
 * @returns {Object|null} Alimento encontrado o null
 */
export const getFoodById = (category, foodId) => {
  if (!foodId) return null;
  
  const categoryMap = {
    'proteina': FOOD_DATABASE.proteinas,
    'carbohidrato': FOOD_DATABASE.carbohidratos,
    'grasa': FOOD_DATABASE.grasas,
    'fruta': FOOD_DATABASE.frutas,
    'vegetal': FOOD_DATABASE.vegetales
  };
  
  const foods = categoryMap[category];
  if (!foods) return null;
  
  return foods.find(f => f.id === foodId) || null;
};

// ============================================================
// HELPER: CALCULAR PORCIÓN PARA UN MACRO ESPECÍFICO
// ============================================================

/**
 * Calcula la porción de un alimento para alcanzar un macro específico
 * Usado para mostrar en los selectores
 * 
 * @param {Object} food - Alimento de la base de datos
 * @param {number} targetMacro - Macro objetivo
 * @param {string} macroType - 'proteinas' | 'carbohidratos' | 'grasas'
 * @returns {Object} { grams, displayGrams }
 */
export const calculatePortionForSelect = (food, targetMacro, macroType) => {
  if (!food || !food[macroType] || food[macroType] <= 0) {
    return { grams: 100, displayGrams: 100 };
  }
  
  const grams = calculateGramsForMacro(targetMacro, food[macroType]);
  
  // Limitar entre 20g y 400g para porciones razonables
  const limitedGrams = Math.max(20, Math.min(400, grams));
  
  return {
    grams: limitedGrams,
    displayGrams: roundGramsForDisplay(limitedGrams)
  };
};

// ============================================================
// HELPER: FILTRAR ALIMENTOS POR ALERGIAS Y PREFERENCIAS
// ============================================================

export { filterFoodsByDietType } from '../data/foodDatabase';

/**
 * Filtra alimentos según las preferencias del usuario
 * @param {Array} foods - Lista de alimentos
 * @param {string} allergies - Alergias separadas por coma
 * @param {string} dislikes - Alimentos que no gustan separados por coma
 * @returns {Array} Alimentos filtrados
 */
export const filterFoodsByPreferences = (foods, allergies = '', dislikes = '') => {
  const excludeTerms = `${allergies} ${dislikes}`
    .toLowerCase()
    .split(/[,\s]+/)
    .filter(t => t.length > 2);
  
  if (excludeTerms.length === 0) return foods;
  
  return foods.filter(food => {
    const foodName = food.nombre.toLowerCase();
    return !excludeTerms.some(term => foodName.includes(term));
  });
};

export default {
  divideMacrosByMeals,
  calculateGramsForMacro,
  calculateProteinPortion,
  calculateCarbPortion,
  calculateSmartFat,
  roundForDisplay,
  roundGramsForDisplay,
  calculateMealPortions,
  getFoodById,
  calculatePortionForSelect,
  filterFoodsByPreferences,
  filterFoodsByDietType
};
